import {
  CustomField,
  SearchPayeeRowsFilterParameter,
  SearchRowEngagementStatusRollup,
  SearchRowTaxDocumentSharePermission
} from "@wingspanhq/wingspan-api-sdk";
import {
  PayeeFilterEligibilityStatus,
  PayeeFilterRequirementStatus,
  PayeeFilters,
  PayeeFilterShareTaxDocument
} from "./types";

/**
 * Maps the URL filter state (`PayeeFilters`) onto the v3 `searchPayeeRows`
 * filter (`SearchPayeeRowsFilterParameter`), the twin of the v1
 * `/search/payee-row` filter. The URL enum *values* are identical strings to the
 * v3 `SearchRow*` enums, so they cast cleanly at this boundary.
 *
 * Not carried over from v1: the `payerId` (Organizations) filter — v3 has no such
 * row filter; a child account is targeted via the `X-Wingspan-Account` scope
 * header instead. See V3_MIGRATION_TRACKER.md (payee-rows §).
 */

function mapShareTaxDocumentRequest(
  filtersValue?: PayeeFilterShareTaxDocument[]
): SearchRowTaxDocumentSharePermission[] {
  const request: SearchRowTaxDocumentSharePermission[] = [];

  (filtersValue || []).forEach(value => {
    switch (value) {
      case PayeeFilterShareTaxDocument.Shared:
        request.push(SearchRowTaxDocumentSharePermission.ALLOW);
        break;
      case PayeeFilterShareTaxDocument.NotShared:
        request.push(
          SearchRowTaxDocumentSharePermission.UNSPECIFIED,
          SearchRowTaxDocumentSharePermission.DECLINE
        );
    }
  });

  return request;
}

/**
 * `isPayeePaymentsEligible` replaces the removed `requirementsStatus` rollup
 * (search `searchRowProjection.ts`): every Activated engagement being
 * payments-eligible, which is what the legacy rollup meant.
 */
function getRequirementsStatusRequest(
  eligibilityStatus?: PayeeFilterEligibilityStatus
): SearchPayeeRowsFilterParameter {
  switch (eligibilityStatus) {
    case PayeeFilterEligibilityStatus.Complete:
      return { isPayeePaymentsEligible: { eq: true } };
    case PayeeFilterEligibilityStatus.Incomplete:
      return { isPayeePaymentsEligible: { eq: false } };
    default:
      return {};
  }
}

/**
 * Per-requirement status → the matching `requirements_is*` boolean flag. The
 * value keeps the v1 `${requirementDefinitionId}_true` convention the search
 * index indexes these flags under.
 */
function getRequirementStatusRequest(
  filtersValue?: PayeeFilterRequirementStatus,
  requirementDefinitionId?: string
): SearchPayeeRowsFilterParameter {
  const value = { eq: `${requirementDefinitionId}_true` };

  switch (filtersValue) {
    case PayeeFilterRequirementStatus.AwaitingContractorSignature:
      return { requirements_isPendingPayeeSignature: value };
    case PayeeFilterRequirementStatus.AwaitingClientSignature:
      return { requirements_isPendingPayerSignature: value };
    case PayeeFilterRequirementStatus.PendingBackgroundCheck:
      return { requirements_isPendingBackgroundCheck: value };
    case PayeeFilterRequirementStatus.ProcessingBackgroundCheck:
      return { requirements_isProcessingBackgroundCheck: value };
    case PayeeFilterRequirementStatus.FailedBackgroundCheck:
      return { requirements_isFailedBackgroundCheck: value };
    case PayeeFilterRequirementStatus.Incomplete:
      return { requirements_isIncomplete: value };
    case PayeeFilterRequirementStatus.Complete:
      return { requirements_isComplete: value };
    case PayeeFilterRequirementStatus.PendingApproval:
      return { requirements_isPendingReview: value };
    case PayeeFilterRequirementStatus.Rejected:
      return { requirements_isRejectedReview: value };
    case PayeeFilterRequirementStatus.Approved:
      return { requirements_isApprovedReview: value };
    case PayeeFilterRequirementStatus.Expired:
      return { requirements_isExpired: value };
    default:
      return {};
  }
}

export const createListRequest = (
  filters: PayeeFilters,
  customFields?: CustomField[]
): { filter: SearchPayeeRowsFilterParameter } => {
  const filter: SearchPayeeRowsFilterParameter = {
    // Default to every non-archived contractor, matching the v1 `!= Inactive`.
    engagementStatus: filters.searchableStatus
      ? {
          eq: filters.searchableStatus
        }
      : { noneOf: [SearchRowEngagementStatusRollup.INACTIVE] },

    ...(filters.engagementId
      ? { engagements_engagementId: { eq: filters.engagementId } }
      : {}),

    ...getRequirementsStatusRequest(filters.eligibilityStatus),

    ...(filters.autoPayStrategy?.length
      ? {
          autoPayStrategy: {
            anyOf: filters.autoPayStrategy
          }
        }
      : {}),

    ...(filters.shareTaxDocument?.length
      ? {
          taxInformationShare: {
            anyOf: mapShareTaxDocumentRequest(filters.shareTaxDocument)
          }
        }
      : {}),

    ...(filters.bulkBatchId ? { batchIds: { eq: filters.bulkBatchId } } : {}),

    ...(filters.searchableTinStatus?.length
      ? {
          tinStatus: { anyOf: filters.searchableTinStatus }
        }
      : {}),

    ...(filters.requirementDefinitionId
      ? { requirements_definitionId: { eq: filters.requirementDefinitionId } }
      : {}),

    ...(filters.requirementDefinitionId && filters.requirementDefinitionVersion
      ? {
          requirements_definitionVersion: {
            eq: filters.requirementDefinitionVersion
          }
        }
      : {}),

    ...getRequirementStatusRequest(
      filters.requirementStatus,
      filters.requirementDefinitionId
    )
  };

  // Custom-field filters use the dynamic `customFields.<key>` path the search
  // endpoint accepts alongside the named fields (string → eq, multi → anyOf).
  // These keys aren't on the typed filter, so they're spread in via a boundary cast.
  const dynamic: Record<string, { eq?: string; anyOf?: string[] }> = {};
  customFields?.forEach(customField => {
    if (!customField.key) {
      return;
    }
    const filterValue = filters[customField.key];
    if (typeof filterValue === "string") {
      dynamic[`customFields.${customField.key}`] = { eq: filterValue };
    } else if (Array.isArray(filterValue) && filterValue.length) {
      dynamic[`customFields.${customField.key}`] = {
        anyOf: filterValue as string[]
      };
    }
  });

  return {
    filter: { ...filter, ...dynamic } as SearchPayeeRowsFilterParameter
  };
};
